import React, { useState, useEffect, useContext } from 'react';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { OnboardingContext } from '../contexts/OnboardingContext';
import { MessageSquare, CheckCircle } from 'lucide-react';
import Logo from '../components/common/Logo';
import Footer from '../components/common/Footer';
import { Button } from '../components/ui';

function OptInPolicyForm() {
  const navigate = useNavigate();
  const { setOnboardingStepIfNotComplete } = useContext(OnboardingContext);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const phoneId = localStorage.getItem('phoneId');

  useEffect(() => {
    if (!phoneId) {
      // If missing phone info, redirect to add-phone
      navigate('/add-phone', { replace: true });
    }
  }, [phoneId, navigate]);

  const handleConfirm = async () => {
    setError('');
    if (!phoneId) {
      setError('Missing phone information.');
      return;
    }
    setLoading(true);

    try {
      const res = await api.post('/api/sms/opt-in', { phoneId });

      if (res.data.success) {

        // Advance onboarding to confirm_phone, then navigate
        //await api.get('/user/onboarding', { step: 'confirm_phone' });
        setOnboardingStepIfNotComplete('confirm_phone');
        console.log('Setting onboarding step to confirm_phone');

        navigate('/confirm-phone');
      } else {
        setError('Failed to opt-in. Please try again.');
      }
    } catch (err) {
      console.error('Opt-in error:', err);
      const msg = err.response?.data?.error || 'Failed to opt-in.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col">
      {/* Header with logo */}
      <div className="w-full py-6 px-4 border-b border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto flex justify-center">
          <Logo size="sm" />
        </div>
      </div>

      {/* Main content - centered single column */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-3xl">
          <div className="mb-6 sm:mb-8 lg:mb-10">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-green-100 p-3 rounded-full">
                <MessageSquare className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 text-center">
              SMS Opt-In Policy
            </h1>
            <p className="text-gray-600 text-center text-base sm:text-lg">
              Please review and accept our SMS terms
            </p>
          </div>

          {error && (
            <div className="mb-6 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              <div className="font-semibold">⚠️ Error</div>
              <p className="mt-1">{error}</p>
            </div>
          )}

            {/* Policy content box */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-6 mb-6 max-h-[300px] sm:max-h-[400px] overflow-y-auto">
              <div className="space-y-4 text-sm sm:text-base text-gray-700">
                <p>
                  By confirming below, you agree to receive recurring SMS messages
                  from SecureTheBag. Message and data rates may apply. Message
                  frequency depends on your notification settings. To stop receiving
                  messages, reply <strong>STOP</strong> at any time. For help, reply <strong>HELP</strong> or contact
                  support.
                </p>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Privacy Policy</h3>
                  <p>
                    We respect your privacy. Your personal data will only be used for SMS 
                    notifications you request. We will not share your phone number with third 
                    parties without your consent.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">SMS Terms</h3>
                  <p>
                    You agree to receive automated text messages, though messages may still be 
                    generated manually under certain circumstances. Your consent is not required 
                    for purchase. You can opt out at any time by replying STOP.
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-900">
                      <strong>Your Rights:</strong> You have the right to opt out at any time. 
                      Standard message and data rates may apply. Message frequency varies based 
                      on your account activity and notification preferences.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Confirm button */}
            <Button
              onClick={handleConfirm}
              variant="primary"
              fullWidth
              disabled={loading}
              loading={loading}
            >
              {loading ? 'Confirming' : 'I Agree - Continue'}
            </Button>

          <p className="mt-4 text-center text-sm text-gray-500">
            By clicking continue, you agree to receive SMS notifications according to the terms above
          </p>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}

// Wrap in OnboardGuard to enforce the opt_in step
export default function OptInPolicy() {
  return <OptInPolicyForm />;
}